// Client API des alertes de supervision.
import { translate } from '../i18n';
import type { TranslationKey } from '../i18n/translations';

/** Fonction de traduction : `t` de useI18n() dans un composant, `translate` ailleurs. */
type Tr = (key: TranslationKey, vars?: Record<string, string | number>) => string;

const BASE_URL = import.meta.env.VITE_WATTELZCE_API_URL || '';

export type AlertSeverity = 'info' | 'warning' | 'critical';
export type AlertOperator = 'gt' | 'lt';

export interface AlertEvent {
  id: number;
  deviceSn: string;
  metric: string;
  severity: AlertSeverity;
  operator: AlertOperator;
  threshold: number;
  valueTriggered: number | null;
  valuePeak: number | null;
  triggeredAt: string;
  resolvedAt: string | null;
  acknowledgedAt: string | null;
  acknowledgedBy: number | null;
  isActive: boolean;
}

export interface AlertRule {
  id: number;
  deviceSn: string;
  metric: string;
  operator: AlertOperator;
  threshold: number;
  deadband: number;
  minDurationS: number;
  severity: AlertSeverity;
  enabled: boolean;
  /** Installation d'appartenance, résolue via device_claims. Null si l'appareil n'est rattaché à aucune. */
  installationId: number | null;
  installationName: string | null;
}

function getHeaders(): HeadersInit {
  const token = localStorage.getItem('dashboard_zce_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 15000);
  try {
    const res = await fetch(`${BASE_URL}${path}`, {
      ...options,
      signal: options?.signal ?? controller.signal,
      headers: { ...getHeaders(), ...(options?.headers || {}) },
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      // seuls les messages métier (chaîne) sont affichés ; une erreur serveur retombe sur le message générique
      const msg = typeof body.error === 'string' ? body.error : undefined;
      throw Object.assign(new Error(msg || `HTTP ${res.status}`), { status: res.status });
    }
    return await res.json();
  } finally {
    clearTimeout(timeout);
  }
}

const METRIC_KEYS: Record<string, TranslationKey> = {
  temperature_c: 'team_metric_temperature_c',
  voltage_v: 'team_metric_voltage_v',
  current_a: 'team_metric_current_a',
  power_kw: 'team_metric_power_kw',
  power_factor: 'team_metric_power_factor',
  frequency_hz: 'team_metric_frequency_hz',
};

/** Libellé traduit d'une mesure (passer `t` depuis un composant). */
export function metricLabel(metric: string, tr: Tr = translate): string {
  const key = METRIC_KEYS[metric];
  return key ? tr(key) : metric;
}

/** Libellés lisibles des mesures surveillées (libellé traduit à la lecture). */
export const METRIC_LABELS: Record<string, { label: string; unit: string }> = {
  temperature_c: { get label() { return metricLabel('temperature_c'); }, unit: '°C' },
  voltage_v: { get label() { return metricLabel('voltage_v'); }, unit: 'V' },
  current_a: { get label() { return metricLabel('current_a'); }, unit: 'A' },
  power_kw: { get label() { return metricLabel('power_kw'); }, unit: 'kW' },
  power_factor: { get label() { return metricLabel('power_factor'); }, unit: '' },
  frequency_hz: { get label() { return metricLabel('frequency_hz'); }, unit: 'Hz' },
};

/** Intitulé d'une alerte, ex. « Surchauffe » / « Tension trop basse ». */
export function alertTitle(a: Pick<AlertEvent, 'metric' | 'operator'>, tr: Tr = translate): string {
  const name = metricLabel(a.metric, tr);
  if (a.metric === 'temperature_c' && a.operator === 'gt') return tr('team_alert_overheat');
  return a.operator === 'gt' ? tr('team_alert_tooHigh', { name }) : tr('team_alert_tooLow', { name });
}

export const alertsApi = {
  list: (historyHours = 24, signal?: AbortSignal): Promise<{ data: AlertEvent[]; unread: number }> =>
    apiFetch(`/api/alerts?historyHours=${historyHours}`, { signal }),

  acknowledge: (id: number): Promise<{ success: boolean }> =>
    apiFetch(`/api/alerts/${id}/ack`, { method: 'POST' }),

  listRules: (deviceSn?: string): Promise<{ data: AlertRule[] }> =>
    apiFetch(`/api/alerts/rules${deviceSn ? `?deviceSn=${encodeURIComponent(deviceSn)}` : ''}`),

  updateRule: (
    id: number,
    body: Partial<Pick<AlertRule, 'threshold' | 'deadband' | 'severity' | 'enabled'>> & { minDurationS?: number },
  ): Promise<{ success: boolean }> =>
    apiFetch(`/api/alerts/rules/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
};

export default alertsApi;
